import type { ErrorType } from 'common/api-calls/common-api-types.ts';
import { toastError, toastSuccess } from 'src/common/toast/utils';
import type { Fn, TAddressInfo } from 'common/types';
import { BASE_PATH, VALIDATION_REGEXS } from 'src/common/constants';
import { convertPersianNumberToEnglishNumber } from 'src/common/utils/numbers';
// todo: remove unused

// todo: remove isServe as this code only runs on clien-side
/** source: https://stackoverflow.com/questions/71258321/how-to-access-client-side-window-method-in-next-js **/
export const isServer = (): boolean => typeof window === 'undefined';
export const hasLocalStorageSupport = (): boolean => 'localStorage' in window;
export const hasSessionStorageSupport = (): boolean => 'sessionStorage' in window;
export const isShareSupported = (): boolean => typeof navigator.share !== 'undefined';

// since superapp sets source_app and source_app_version in the url, we can use this to check if the app is from superapp
export const isFromSupperApp = () => {
  const sourceApp = new URLSearchParams(window.location.search).get('source_app');
  const sourceAppVersion = new URLSearchParams(window.location.search).get('source_app_version');
  return sourceApp && sourceAppVersion;
};

const SUPER_APP_ROOT = 'https://app.tapsi.cab/';
export const exitApp = () => {
  try {
    if (isWebView()) {
      window.WebViewInterface?.close();
      return;
    }
    window.location.replace(SUPER_APP_ROOT);
  } catch {
    window.location.replace(SUPER_APP_ROOT);
  }
};

export const isAppInBackground = (): boolean => {
  if ('hidden' in document) return document.hidden;

  const doc = document as Document & { webkitHidden?: boolean; msHidden?: boolean };
  if ('webkitHidden' in doc) return Boolean(doc.webkitHidden);
  if ('msHidden' in doc) return Boolean(doc.msHidden);
  // If the browser doesn't support the Page Visibility API, assume it is NOT hidden.
  return false;
};

export const isAndroid = (): boolean => /android/i.test(navigator.userAgent);
export const isWebView = (): boolean => {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();

    // iOS detection
    const isIOS = /iphone|ipod|ipad/.test(ua);
    const isIOSWebview =
      isIOS &&
      !/safari/.test(ua) && // Safari keyword missing
      !/crios/.test(ua) && // Not Chrome on iOS
      !/fxios/.test(ua); // Not Firefox on iOS

    // Android detection
    const isAndroid = /android/.test(ua);
    const isAndroidWebview = isAndroid && (ua.indexOf('wv') > -1 || ua.indexOf('version/') > -1);

    return isIOSWebview || isAndroidWebview;
  } catch (e) {
    return typeof window.WebViewInterface != 'undefined';
  }
};
export const isProductionEnv = () => import.meta.env.VITE_APP_ENV === 'production';
export const isDesktop = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
};

export const isValidPhoneNumber = (number: string): boolean => /^((0|\+98)9)[0-9]{9}$/.test(number);
export const isValidAdditionalPhoneNumber = (number: string): boolean => !number || /^0[0-9]{10}$/.test(number);

/**
 * @function debounce
 * @description
 * # Debounce
 *
 * @example
 *   const handleMouseMove = debounce((ev) => {
 *       // Do stuff with the event!
 *   }, 250);
 *   window.addEventListener('mousemove', handleMouseMove);
 * @link https://www.joshwcomeau.com/snippets/javascript/debounce
 * @note
 *  - Note that this example is focused on vanilla JS. If you're using React, you'll want to wrap your handler in useMemo, so that it doesn't get re-generated on every render
 */
export function debounce<Input extends Array<unknown>, Output = unknown>(callback: (...i: Input) => Output, wait: number) {
  let timeoutId: number | undefined = undefined;
  const debouncedFn = (...args: Input) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      // eslint-disable-next-line prefer-spread
      callback.apply(null, args);
    }, wait);
  };

  debouncedFn.cancel = () => {
    window.clearTimeout(timeoutId);
    timeoutId = undefined;
  };

  return debouncedFn;
}

export const getOnError =
  (defaultMessage = '') =>
  (error?: ErrorType) => {
    if (error?.code == 'AUTHENTICATION_ERROR') return;
    const err = String(error?.message || error?.code || defaultMessage);
    toastError(err);
  };

export const sanityCheckQueryParamValue = (str: string) => /^[A-Za-z0-9-_=.#*]*$/.test(str);

// core duplicate with changes
export function getQueriedUrl(
  baseUrl: string,
  parameters: { [key: string | number | symbol]: unknown },
  options?: {
    stringify?: boolean;
    removeNulls?: boolean;
    removeUnsafe?: boolean;
  },
) {
  const url = new URL(baseUrl, window.location.origin);
  const searchParams = new URLSearchParams();

  Object.entries(parameters).forEach(([key, value]) => {
    if (value === undefined || (options?.removeNulls && value === null)) return;

    if (Array.isArray(value)) {
      if (value.some((item) => typeof item === 'object' && item !== null)) {
        const paramValue = JSON.stringify(value);
        if (options?.removeUnsafe && !sanityCheckQueryParamValue(paramValue)) return;
        searchParams.set(key, paramValue);
      } else {
        value.forEach((item) => {
          const paramValue = String(item);
          if (!options?.removeUnsafe || sanityCheckQueryParamValue(paramValue)) {
            searchParams.append(key, paramValue);
          }
        });
      }
      return;
    }

    const paramValue = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);

    if (options?.removeUnsafe && !sanityCheckQueryParamValue(paramValue)) return;

    searchParams.set(key, paramValue);
  });

  const query = searchParams.toString();
  return query ? `${url.pathname}?${query}` : url.pathname;
}

export function removeQueryFromUrl(url: string, queryToRemove: string): string {
  if (!url.includes('?')) return url;
  const path = url.split('?')[0];
  const queries = url
    .split('?')[1]
    .split('&')
    .filter((query) => !query.includes(queryToRemove))
    .map((queryWithValue) => ({
      key: queryWithValue.split('=')[0],
      value: queryWithValue.split('=')[1] || '',
    }));
  if (!queries?.length) return path;
  const formattedQueryParam = queries.reduce((acc, cur) => ({ ...acc, ...{ [cur.key]: cur.value } }), {});
  return getQueriedUrl(path, formattedQueryParam, { stringify: true });
}

export const removeWhiteSpaces = (str: string) => str.trim().replace(/ /g, '');

export const includesUnsafeUrlCharacters = (url: string) => {
  const unsafeCharacters = ['<', '>', '(', ')', '}', '{', '~', '^', '+', '*', '`', '[', ']', '\\', '|'];
  return unsafeCharacters.some((char) => url.includes(char));
};

// core duplicate - with some changes (handle development and check origin)
export const isSafeUrl = (tempUrl: string) => {
  const url = removeWhiteSpaces(tempUrl);
  if (includesUnsafeUrlCharacters(url)) return false;
  const safeDomains = ['tapsi.ir', 'tapsi.cab', 'tapsi.taxi', 'tapsi.org', 'tap30.net'];
  try {
    const parsedUrl = new URL(url);
    const domain = parsedUrl.hostname;
    if (safeDomains.some((safeDomain) => domain.endsWith(safeDomain))) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

export function getFormattedPhoneNumber(phoneNumber: string) {
  const englishPhoneNumber = convertPersianNumberToEnglishNumber(phoneNumber);
  // Remove all non-digit characters
  const digits = englishPhoneNumber.replace(/\D/g, '');

  // // If the cleaned digits already match the phone number format, return them
  if (VALIDATION_REGEXS.PHONE_NUMBER.test(digits)) {
    return digits;
  }

  // If input is exactly 10 digits and starts with "9", prepend "0"
  if (/^9\d{9}$/.test(digits)) {
    const candidate = '0' + digits;
    if (VALIDATION_REGEXS.PHONE_NUMBER.test(candidate)) return candidate;
  }

  // Handle +98 and 0098 prefixes, replace with "0"
  if (/^(98|0098)/.test(digits)) {
    const candidate = digits.replace(/^(98|0098)/, '0');
    if (VALIDATION_REGEXS.PHONE_NUMBER.test(candidate)) return candidate;
  }
  return phoneNumber;
}

export const isSamePhoneNumber = (phoneNumber1?: string, phoneNumber2?: string): boolean => {
  if (!phoneNumber1 || !phoneNumber2) return false;
  return getFormattedPhoneNumber(phoneNumber1) === getFormattedPhoneNumber(phoneNumber2);
};

export const formatTehranPhoneNumber = (phoneNumber: `021${string}`): `${string}-${string}` =>
  `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, phoneNumber.length)}`;

type TGetQueryValueOptions = {
  safetyCheck?: boolean;
  removeEmpty?: boolean;
  customQueryString?: string;
};
export const getValueOfQueryKeys = (keys: string[], options: TGetQueryValueOptions = {}): Record<string, string> => {
  const queryString = options?.customQueryString || window.location.search;
  return keys.reduce((res, key) => {
    let value = queryString.split(`${key}=`)?.[1]?.split('&')[0];
    if (options?.safetyCheck) value = sanityCheckQueryParamValue(value) ? value : '';
    if (options?.removeEmpty) value = value && value !== 'undefined' && value !== 'null' ? value : '';
    return value ? { ...res, [key]: value } : res;
  }, {});
};

export function snakeToCamelCase(status: string) {
  return status.toLowerCase().replace(/_./g, (x) => x[1].toUpperCase());
}

export function getStatusMetadataKey<TStatusKeys extends string>(status: TStatusKeys, stringTemplate = 'Metadata'): string {
  return status ? `${snakeToCamelCase(status)}${stringTemplate}` : status;
}

export const onCopy = (text: string, customMessage = 'کپی شد.') => {
  navigator?.clipboard?.writeText(text);
  toastSuccess(customMessage, { duration: 3000 });
};

/**
 * @function isCookieEnabled
 * @description
 * ## isCookieEnabled
 * This method infers if the client can use cookie or not based on the following conditions (all of them must be met):
 * - the environment is the browser: using the `isServer` method
 * - cookie is enabled in the browser: using `navigator.cookieEnabled`
 * @return boolean
 * **/
export const isCookieEnabled = (): boolean => {
  return !isServer() && navigator?.cookieEnabled;
};

/**
 * @function getEnumFromObject
 * @description
 * # getEnumFromObject
 * This function Creates an enum from an Object keys in which enum values are also the keys.
 * @param obj the object to create enum from
 * @returns a Record that can be used as an enum
 */
export function getEnumFromObject<T extends string>(obj: object): Record<T, T> {
  return Object.fromEntries(Object.keys(obj).map((key) => [key, key])) as unknown as Record<T, T>;
}

export const removeLeadingZerosFromTime = (timeStr: string): string => timeStr.replace(/^0(?:0:0?)?/, '');

export const getRandom = (min: number, max: number): number => {
  const minFloor = Math.ceil(min);
  const maxFloor = Math.floor(max);
  return Math.floor(Math.random() * (maxFloor - minFloor + 1)) + minFloor;
};

export const minutesToMillis = (minutes: number | string): number => Number(minutes) * 1000 * 60;
export const millisToMinute = (ms: number | string): number => Number(ms) / 1000 / 60;
export const secondsToMillis = (s: number | string): number => Number(s) * 1000;

export const call = (phoneNumber: number | string | undefined): Fn =>
  phoneNumber ? () => window.location.assign(`tel:${phoneNumber}`) : () => {};
export const sms =
  (phoneNumber: number | string): Fn =>
  () =>
    window.location.assign(`sms:${phoneNumber}`);

/**
 * @function getFormattedAddress
 *
 * @param {TAddressInfo?} data - {address?: string, apartmentNumber?: string, buildingNumber?: string}.
 *
 * @returns {string} - formatted address string: ${AddressInfoData.address}, پلاک ${AddressInfoData.buildingNumber}, واحد ${AddressInfoData.apartmentNumber}.
 *
 * @note
 *
 * - When the data is not available, the function returns an **empty string**.
 * - This function also handles removing پلاک and واحد indicators when the respective data is not available.
 */
export const getFormattedAddress = (data?: Partial<TAddressInfo>): string => {
  if (!data) return '';
  const address = data?.address ?? '';
  const building = data?.buildingNumber ? 'پلاک ' + data.buildingNumber : '';
  const apartment = data?.apartmentNumber ? 'واحد ' + data.apartmentNumber : '';
  return address + (building ? '، ' + building : '') + (apartment ? '، ' + apartment : '');
};

/**
 * @function getFullAddressTo
 * @description
 * # getFullAddressTo
 * Returns the current domain of the application, concatenated with the input path.
 * In other words, returns the full address to a path of your application.
 * @example
 * getFullAddressTo('/some/place'); // https://example.ir/some/place
 * getFullAddressTo('some/place'); // https://example.ir/some/place
 * @example
 * getFullAddressTo() // https://example.ir/
 * getFullAddressTo('', '/app') // https://example.ir/app/
 * getFullAddressTo('/', '/app') // https://example.ir/app/
 * @param {string?} somePath - the path you want the full address to.
 * @param {string?} basePath - adds a base path to the address.
 * @returns {string} - A Url indicating the full address of a page in your app (protocol + domain + base_path + path)
 * @note
 * - The presence or absence of the leading slash is handled
 * - The trailing slash is not handled. Some inputs may generate a trailing slash and some may not.
 */
export const getFullAddressTo = (somePath?: string, basePath?: string): string => {
  const pathname = somePath?.replace(/^\//, '') ?? '';
  const base = basePath?.replace(/^\//, '') ?? '';
  const path = base ? `${base}/${pathname}` : pathname;
  const url = new URL(String(window.location));
  return `${url.protocol}//${url.host}/${path}`;
};

/**
 * A simple asynchronous lock (Mutex) implementation using Promises.
 * This class allows you to create a lock that can be acquired asynchronously
 * and released by calling the `unlock` method.
 * {
 *   // Release the lock
 *   mutex.unlock();
 * }
 * @note It is crucial to call `unlock` after acquiring the lock to avoid deadlocks.
 * Failing to unlock will leave the lock permanently acquired
 */
export class Mutex {
  /**
   * The function to call to release the lock.
   * This is set when the lock is created and is used to resolve the `lock` Promise.
   */
  unlock!: (value?: unknown) => void;

  /**
   * A Promise that resolves when the lock is acquired.
   * Await this Promise to acquire the lock.
   */
  lock: Promise<unknown>;

  constructor() {
    this.lock = new Promise((resolve) => {
      this.unlock = resolve;
    });
  }
}
/**
 * Returns the full path to an icon located in the static icons directory.
 *
 * This utility ensures consistent icon path generation across the application.
 * Developers **must use this function** instead of manually passing the icon path
 * to `<img>` or other image-rendering elements to avoid hardcoding and ensure
 * maintainability.
 *
 * @param {string} iconName - The name of the icon file (without extension).
 * @param {string} extension - The extension of the icon file (default is svg)
 * @returns {string} The full path to the icon SVG file.
 *
 * @example
 * ```tsx
 * <img src={getPathToIcon('user')} alt="User icon" />
 * ```
 */
export const getPathToIcon = (iconName: string, extension?: string) => {
  return `${BASE_PATH}static/icons/${iconName}.${extension ?? 'svg'}`;
};

/**
 * @function getObjFromUrlParam
 * @description
 * # getObjFromUrlParam
 * Reads a JSON stringified value from URL parameters and parses it to the specified type.
 * @param {string} key - The URL parameter key to read from
 * @returns {T | undefined} - The parsed object of type T, or undefined if not found or invalid
 * @example
 * const senderData = getObjFromUrlParam<TTransceiver>('sender');
 * const receiversData = getObjFromUrlParam<TTransceiver[]>('receivers');
 */
export const getObjFromUrlParam = <T>(key: string): T | undefined => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get(key);

    if (!value) {
      return undefined;
    }

    return JSON.parse(value) as T;
  } catch (error) {
    console.warn('Failed to parse URL parameter:', key, error);
    return undefined;
  }
};

/**
 * @function setObjToUrlParam
 * @description
 * # setObjToUrlParam
 * Sets a JSON stringified value to URL parameters and updates the browser URL.
 * This function preserves all existing URL parameters and only modifies the specified key.
 * @param {string} key - The URL parameter key to set
 * @param {T} value - The object to stringify and set as the parameter value
 * @param {boolean} replace - Whether to replace the current history entry (default: true)
 * @example
 * setObjToUrlParam('sender', senderData);
 * setObjToUrlParam('receivers', receiversData, false);
 */
export const setObjToUrlParam = <T>(key: string, value: T, replace: boolean = true): void => {
  try {
    const url = new URL(window.location.href);
    const stringifiedValue = JSON.stringify(value);

    // Only set the specific parameter, preserving all others
    url.searchParams.set(key, stringifiedValue);

    // Use a more careful approach to update the URL
    const newUrl = url.toString();

    if (replace) {
      window.history.replaceState(window.history.state, '', newUrl);
    } else {
      window.history.pushState(window.history.state, '', newUrl);
    }
  } catch (error) {
    console.warn('Failed to set URL parameter:', key, error);
  }
};

export const isInNPercent = (number: number, n: number) => {
  return number % 101 < n;
};

/**
 * Preloads a list of image URLs by creating Image objects and assigning their
 * `src`, prompting the browser to fetch and cache them ahead of time.
 * Useful for warming the browser cache so images render instantly when later used in the DOM.
 *
 * @param {string[]} urls - Array of image URLs to preload.
 * @returns {void}
 */
export const preLoadImageUrls = (urls: string[]) => {
  urls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
};
